import React, { useEffect, useRef } from "react";
import { FormattedMessage } from "react-intl";
import { Link, useNavigate, useOutletContext } from "react-router-dom";

import EmptyQueryHandler from "@components/empty-query-handler/empty-query-handler";
import ChevronRightSVG from "@components/icons/chevron-right-svg";
import ChevronSVG from "@components/icons/chevron-svg";
import ItemExercise from "@components/item-exercise/item-exercise";
import ItemLesson from "@components/item-lesson/item-lesson";
import WrapperItemCourse from "@components/wrapper-item-course/wrapper-item-course";

import { formatUrlWithLocale } from "@helpers/format-url-with-locale";
import { addDividerOnModal } from "@helpers/helpers";

import { useGetCourseQuery } from "../../api/course";
import { useGetExerciseEntrypointQuery } from "../../api/exercise-entrypoint";

function SelectExercise() {
  const navigate = useNavigate();

  const { data } = useGetExerciseEntrypointQuery({
    exerciseId: 0,
  });

  const { data: courses } = useGetCourseQuery({
    courseId: data!.user.courseId,
  });

  const modalBody = useRef<HTMLDivElement>(null);
  const hr = useRef<HTMLHRElement>(null);

  const { closeModal } = useOutletContext<{
    closeModal?: () => void;
  }>();

  useEffect(() => {
    addDividerOnModal(modalBody, hr);

    const close = (event: any) => {
      if (event.keyCode === 27) {
        navigate(formatUrlWithLocale("/typing-tutor/settings/"));
      }
    };
    window.addEventListener("keyup", close);

    return () => window.removeEventListener("keyup", close);
  }, [navigate]);

  return (
    <EmptyQueryHandler isLoading={!courses}>
      <div className='modal-header p-4 pb-0 md:p-6 md:pb-0'>
        <div className='relative text-center'>
          <button
            type='button'
            onClick={() =>
              navigate(formatUrlWithLocale("/typing-tutor/settings/"))
            }
            className='absolute top-0 left-0'
          >
            <ChevronSVG
              width={30}
              height={30}
              classNameProps='fill-icon-secondary dark:fill-icon-dark-secondary'
            />
          </button>
          <h3>
            <FormattedMessage id='select-exercise' />
          </h3>
        </div>
      </div>
      <hr
        className='mx-4 mt-1 border-t-2 border-transparent transition-all md:mx-6'
        ref={hr}
      />
      <div className='modal-body p-4 pt-0 md:p-6 md:pt-0' ref={modalBody}>
        <div className='mb-3 md:mb-4'>
          <Link
            to={formatUrlWithLocale("/typing-tutor/select-course/")}
            className='not-prose mb-3 flex justify-between md:mb-4'
          >
            {courses ? (
              <WrapperItemCourse
                course={courses && courses.course}
                className='mr-9'
              />
            ) : null}
            <ChevronRightSVG
              classNameProps='fill-icon-primary dark:fill-icon-dark-primary shrink-0 mt-0.5 mr-1 ml-2'
              height={22}
              width={14}
            />
          </Link>
        </div>
        <hr className='dark:border-brd-dark-primary border-t-2 border-brd-primary' />
        {courses &&
          courses.lessons.map((lesson, index) => (
            <div key={lesson.id} className='relative'>
              <ItemLesson index={index} lesson={lesson} />
              <div className='-mx-1 flex flex-wrap'>
                {lesson.exercises.map((exercise) => (
                  <ItemExercise
                    key={exercise.id}
                    exercise={exercise}
                    lessonComplete={lesson.isLessonComplete}
                    closeModal={closeModal}
                  />
                ))}
              </div>
            </div>
          ))}
      </div>
    </EmptyQueryHandler>
  );
}

export default SelectExercise;
